// src/components/LogForm.jsx

import React, { useState, useEffect } from 'react';
import client from '../api/client';
import CreditType from '../components/CreditType';
import { formatDate, formatDatetime, getNextWeekday } from '../utils/dateHelpers';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import AutosuggestInput from './AutosuggestInput';

dayjs.locale('ja');

export default function LogForm({
  actor,
  onCreated,
  prefill,
  clearPrefill,
  initialData,
  onCancel,
  isAdmin = false,
  beginBusy = () => () => {},
  uiLocked = false
}) {
  const isEdit = Boolean(initialData);
  const [recordType, setRecordType] = useState(prefill.recordType);
  const [content, setContent] = useState(prefill.content);
  const [minutes, setMinutes] = useState(prefill.minutes);
  const [amount, setAmount] = useState(prefill.amount);
  const [amountUnit, setAmountUnit] = useState('');
  const [minutesEnabled, setMinutesEnabled] = useState(true);

  // フォームフィールド
  const [evaluation, setEvaluation] = useState('');
  const [descriptions, setDescriptions] = useState([]);
  const [rules, setRules] = useState([]);
  const [creditType, setCreditType] = useState('');
  const [creditTypeRep, setCreditTypeRep] = useState('');
  const [targetDate, setTargetDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [targetDateRep, setTargetDateRep] = useState('');
  const [startTime, setStartTime] = useState('');
  const [startTimeSelector, setStartTimeSelector] = useState(false);
  const [endTime, setEndTime] = useState('');
  const [endTimeSelector, setEndTimeSelector] = useState(false);
  const [normTarget, setNormTarget] = useState('');
  const [normDone, setNormDone] = useState(false);
  const [locked, setLocked] = useState(prefill.recordType !== 'activity');
  const [penaltyType, setPenaltyType] = useState(0);
  const [penaltyAmount, setPenaltyAmount] = useState('60');
  const [penaltyRate, setPenaltyRate] = useState('10');
  const [penaltyPeriod, setPenaltyPeriod] = useState('');
  const [contentHistory, setContentHistory] = useState([]);
  const [contentHistoryActivity, setContentHistoryActivity] = useState([]);
  const [creditTypeHistory, setCreditTypeHistory] = useState([]);

  // 送信状態
  const [requesting, setRequesting] = useState(false);

  // 送信失敗時のメッセージ（通信失敗などの「フォーム全体」エラー）
  const [submitError, setSubmitError] = useState('');

  // エラー状態
  const [errors, setErrors] = useState({});

  // prefill が変わったら上書き
  useEffect(() => {
    if (prefill.recordType) {
      setRecordType(prefill.recordType);
      setContent(prefill.content);
      setMinutes(prefill.minutes);
      setAmount(prefill.amount);
    }
  }, [prefill]);

  useEffect(() => {
    if (isEdit) {
      setRecordType(initialData.record_type);
      setContent(initialData.content);
      if (isAdmin) {
        setLocked(initialData.locked);
      }
      if (initialData.record_type === 'activity') {
        setMinutes(initialData.effort_minutes?.toString() || '');
        setAmount(initialData.effort_amount?.toString() || '');
        setCreditType(initialData.credit_type);
        setTargetDate(initialData.target_date);
        if (isAdmin) {
          setStartTimeSelector(initialData.start_time !== null);
          setStartTime(initialData.start_time);
        }
      }
      if (initialData.record_type === 'replace') {
        setMinutes(initialData.effort_minutes?.toString() || '');
        if (initialData.credit_type2) {
          setCreditType(initialData.credit_type2);
          setCreditTypeRep(initialData.credit_type);
        } else {
          setCreditType(initialData.credit_type);
          setCreditTypeRep("");
        }
        if (initialData.start_time && initialData.start_time !== "") {
          setTargetDate(initialData.start_time.replace(/[ T].*$/, ""));
          setTargetDateRep(initialData.target_date);
        } else {
          setTargetDate(initialData.target_date);
          setTargetDateRep("");
        }
      }
      if (initialData.record_type === 'norm') {
        setNormTarget(initialData.norm_target || '');
        setNormDone(initialData.norm_done || false);
        setStartTimeSelector(initialData.start_time != null);
        setStartTime(initialData.start_time);
        if (isAdmin) {
          setEndTimeSelector(initialData.end_time != null);
          setEndTime(initialData.end_time);
        }
      }
      if (initialData.record_type === 'penalty') {
        if (initialData.penalty_amount) {
          setPenaltyType(0)
          setPenaltyAmount(initialData.penalty_amount?.toString() || '60');
          setPenaltyRate('')
          setPenaltyPeriod("1")
        } else {
          setPenaltyType(1)
          setPenaltyAmount('');
          setPenaltyRate(initialData.penalty_rate ? (initialData.penalty_rate * 100).toString() : '10')

          var base_time = initialData.start_time ? dayjs(initialData.start_time) : dayjs();
          var diff = 1;
          if (initialData.end_time) {
            diff = dayjs(initialData.end_time).diff(base_time, "d", true);
            diff = Math.ceil(diff).toFixed();
          }
          setPenaltyPeriod(diff.toString())
        }
        setStartTimeSelector(initialData.start_time != null);
        setStartTime(initialData.start_time);
      }
      if (isAdmin && initialData.evaluation != null) {
        setEvaluation(initialData.evaluation);
      }
    }
  }, [initialData, isEdit, isAdmin]);

  useEffect(() => {
    if (recordType === 'activity') {
      // コンテンツ履歴取得 (Activity)
      client
        .get(`/logs/${actor}/history?field=content&record_type=activity`)
        .then(res => {
          setContentHistory(res.data.history.filter(item => item.is_rule));
        })
        .catch(() => { /* ignore */ });

      setContentHistoryActivity([]);
    } else if (recordType === 'norm') {
      // コンテンツ履歴取得
      client
        .get(`/logs/${actor}/history?field=content&record_type=norm`)
        .then(res => setContentHistory(res.data.history))
        .catch(() => { /* ignore */ });

      // コンテンツ履歴取得 (Activity)
      client
        .get(`/logs/${actor}/history?field=content&record_type=activity`)
        .then(res => setContentHistoryActivity(res.data.history.filter(item => item.is_rule)))
        .catch(() => { /* ignore */ });
    } else if (recordType === 'penalty') {
      // コンテンツ履歴取得
      client
        .get(`/logs/${actor}/history?field=content&record_type=penalty`)
        .then(res => setContentHistory(res.data.history))
        .catch(() => { /* ignore */ });

      setContentHistoryActivity([]);
    } else {
      setContentHistory([]);
      setContentHistoryActivity([]);
    }

    // credit_type 履歴取得
    client
      .get(`/logs/${actor}/history?field=credit_type`)
      .then(res => {
        if (!isEdit && res.data.history.length > 0 && creditType === '') {
          setCreditType(res.data.history[0].value);
        }
        setCreditTypeHistory(res.data.history);
      })
      .catch(() => { /* ignore */ });
  }, [actor, recordType]);

  // content が変わったら amountUnit, descriptions, rules を更新
  useEffect(() => {
    var amountUnitTmp = '';
    var descs = [];
    var rules = [];
    if (recordType === 'activity') {
      for (let i = 0; i < contentHistory.length; i++) {
        let rule = contentHistory[i];
        if (rule.is_rule && content.indexOf(rule.value) >= 0) {
          amountUnitTmp = rule.amount_unit;
          descs = rule.descs;
          rules = rule.rules;
          break;
        }
      }
    }

    setAmountUnit(amountUnitTmp);
    setDescriptions(descs);
    setRules(rules);
    setMinutesEnabled(
      rules.length == 0 ||
      rules.find((x) => x.result_target > 0) != null /* null か undefined だったら false */
    );
  }, [content, contentHistory]);

  const validate = () => {
    const F_CONTENT     = 0x01;
    const F_MINUTES     = 0x02;
    const F_AMOUNT      = 0x04;
    const F_TARGET_DATE = 0x08;
    const F_PEN_AMOUNT  = 0x10;
    const F_PEN_RATE    = 0x20;
    const VAL_FLAGS = {
      activity: F_CONTENT | F_AMOUNT | F_TARGET_DATE | (minutesEnabled ? F_MINUTES : 0),
      replace :                        F_TARGET_DATE | F_MINUTES,
      norm    : F_CONTENT,
      penalty : F_CONTENT | ((penaltyType === 0) ? F_PEN_AMOUNT : F_PEN_RATE),
    };

    const flags = VAL_FLAGS[recordType];
    const e = {};

    if (flags & F_CONTENT) {
      if (!content.trim()) {
        e.content = '内容は必須です。';
      }
    }
    if (flags & F_MINUTES) {
      if (!minutes) {
        e.minutes = '活動時間（分）は必須です。';
      } else if (!/^\d+(\.\d+)?$/.test(minutes) || Number(minutes) < 0) {
        e.minutes = '0以上の数を入力してください。';
      }
    }
    if (flags & F_AMOUNT) {
      if (amountUnit) {
        if (!amount) {
          e.amount = `取り組み量 (${amountUnit}) は必須です。`;
        } else if (!/^\d+(\.\d+)?$/.test(amount) || Number(amount) < 0) {
          e.amount = '0以上の数を入力してください。';
        }
      }
    }
    if (flags & F_TARGET_DATE) {
      if (!targetDate) {
        e.targetDate = '反映日は必須です。';
      } else if (!dayjs(targetDate).isValid()) {
        e.targetDate = '反映日は YYYY-MM-DD 形式で入力してください。';
      }
    }
    if (flags & F_PEN_AMOUNT) {
      if (!penaltyAmount) {
        e.penaltyAmount = '減算時間は必須です。';
      } else if (!/^\d+(\.\d+)?$/.test(penaltyAmount) || Number(penaltyAmount) < 0) {
        e.penaltyAmount = '0以上の数値を入力してください。';
      }
    }
    if (flags & F_PEN_RATE) {
      if (!penaltyRate) {
        e.penaltyAmount = '効率低下率は必須です。';
      } else if (!/^[+-]?\d+(\.\d+)?$/.test(penaltyRate)) {
        e.penaltyAmount = '数値を入力してください。';
      }
    }

    if (recordType === 'replace') {
      if (!isAdmin && targetDate && targetDateRep) {
        var from = dayjs(targetDate).startOf('day');
        var to = dayjs(targetDateRep).startOf('day');
        if (!to.isValid()) {
          e.targetDateRep = '移転日は YYYY-MM-DD 形式で入力してください。';
        } else if (from.day() === 0 && from.diff(to) !== 0) {
          e.targetDateRep = '日曜日の時間を他の日に移転することは禁止です';
        }
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    const payload = isEdit
      ? { id: initialData.id }
      : { record_type: recordType };

    if (recordType === 'activity') {
      payload.content = content;
      payload.effort_minutes = minutesEnabled ? Number(minutes) : undefined;
      payload.effort_amount = amount ? Number(amount) : undefined;
      payload.credit_type = creditType;
      payload.target_date = targetDate;
      if (startTime)
        payload.start_time = startTime;
    }

    if (recordType === 'replace') {
      payload.content = content;
      payload.effort_minutes = Number(minutes);

      if (creditTypeRep !== "") {
        payload.credit_type = creditTypeRep;
        payload.credit_type2 = creditType;
      } else {
        payload.credit_type = creditType;
        payload.credit_type2 = "";
      }
      if (targetDateRep !== "") {
        payload.start_time = targetDate + " 00:00:00";
        payload.target_date = targetDateRep;
      } else {
        payload.target_date = targetDate;
      }
    }

    if (recordType === 'norm') {
      payload.content = content;
      payload.norm_target = normTarget;
      if (isEdit && isAdmin) {
        payload.norm_done = normDone;
      }
      payload.start_time = (startTimeSelector && startTime) ? startTime : null;
      payload.end_time = (endTimeSelector && endTime) ? endTime : null;
    }

    if (recordType === 'penalty') {
      payload.content = content;
      if (penaltyType === 0) {
        payload.penalty_amount = Number(penaltyAmount);
        payload.penalty_rate = null;
        payload.start_time = (startTimeSelector && startTime) ? startTime : null;
        payload.end_time = null;
      } else {
        payload.penalty_rate = Number(penaltyRate) / 100.0;
        payload.penalty_amount = null;

        var base_time;
        if (startTimeSelector && startTime) {
          base_time = dayjs(startTime).startOf('day');
          payload.start_time = formatDatetime(base_time);
        } else {
          base_time = dayjs().startOf('day');
          payload.start_time = null;
        }
        payload.end_time = formatDatetime(base_time.add(Number(penaltyPeriod), 'd'));
      }
    }

    // 管理者モードなら評価も送信
    if (isAdmin) {
      payload.evaluation = evaluation.trim() || null;
      payload.locked = locked;
    }

    const url = isAdmin
      ? `/admin/logs/${actor}`
      : `/logs/${actor}`;

    const endBusy = beginBusy();
    setRequesting(true);
    try {
      await client[isEdit ? 'put' : 'post'](url, payload);
      onCreated(payload);
      if (!isEdit) clearPrefill();  // 事前入力をクリア
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        '送信に失敗しました。ネットワーク状況を確認して、もう一度お試しください。';
      setSubmitError(String(msg));
    } finally {
      setRequesting(false);
      endBusy();
    }
  };


  function RowLabel({
    label,
    required
  }) {
    return (
      <label className="row_label">
        <span>{label.map(label1 => (<div key={label1}>{label1}</div>))}</span>
        {required && (<span className="required"/>)}
      </label>
    );
  }

  const getReward = (rule, result) => {
    let val = rule.reward_target * Math.pow(result, rule.reward_power) / Math.pow(rule.result_target, rule.reward_power);
    return rule.reward_limit && val > rule.reward_limit ? rule.reward_limit : val;
  };

  // Rule に応じて報酬のガイドメッセージを表示する
  function RewardGuide({
    rules,
    result_type
  }) {
    if (rules.length == 0) {
      return "";
    }

    if (result_type == "") {
      return <div className="rule">
        {rules.map((rule, idx) => <React.Fragment key={idx}>
          {
            (rule.reward_type === '報酬時間')
            ? <>
                <span className="unit">報酬</span><span> </span>
                <span>+{Math.round(rule.reward_target)}</span><span className="unit">分</span>
              </>
            : (rule.reward_type === '報酬効率')
            ? <>
                <span className="unit">効率</span><span> </span>
                <span>+{(rule.reward_target * 100).toFixed(0)}</span><span className="unit">%</span>
              </>
            : <>
                <span className="unit">rule.reward_type</span> <span>rule.reward_target</span>
              </>
          }
          {rule.end_delta >= 0 && <span> ({rule.end_delta}<span className="unit">日間</span>)</span>}
        </React.Fragment>)}
      </div>;
    }

    let result_vals = (
      result_type === '活動時間' ? [15, 30, 45, 60, null, 90, null, 120, null] :
                                  [1, 2, 3, 4, null, 10, null, 20, null]
    );

    return <table className="rule">
      <thead>
        <tr>
          <th><span className="unit">実績</span></th>
          {result_vals.map((val, idx) => <th key={idx}>{
            val == null
            ? ""
            : <>
                {val}
                {result_type === '活動時間' ? <span className="unit">分</span> :
                result_type === '成果量' ? <span className="unit">{rules[0].amount_unit}</span> : ""}
              </>
          }</th>)}
          <th>...</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rules.map(rule => 
          (rule.reward_type === '報酬時間')
          ? <tr>
              <td><span className="unit">報酬</span></td>
              {result_vals.map(val =>
                val == null ? <td></td> : 
                <td><span>+{Math.round(getReward(rule, val))}</span><span className="unit">分</span></td>
              )}
              <td>...</td>
              <td></td>
            </tr>
          : (rule.reward_type === '報酬効率')
          ? <tr>
              <td><span className="unit">効率</span></td>
              {result_vals.map(val =>
                val == null ? <td></td> : 
                <td><span>+{(getReward(rule, val) * 100).toFixed(0)}</span><span className="unit">%</span></td>
              )}
              <td>...</td>
              <td>
                {/*rule.reward_limit > 0 && <span className="marginbox">
                  <span className="unit">最大</span>{(rule.reward_limit * 100).toFixed(0)}<span className="unit">%</span>
                </span>*/}
                {rule.end_delta >= 0 && (<span>
                  {rule.end_delta}<span className="unit">日間</span>
                </span>)}
              </td>
            </tr>
          : <tr>
              {result_vals.map(val =>
                val == null ? <td></td> : 
                <td><span>+{Math.round(getReward(rule, val))}</span></td>
              )}
              <td>...</td>
              <td></td>
            </tr>
        )}
      </tbody>
    </table>;
  }


  return (
    <form onSubmit={handleSubmit} className="log-form">
      {submitError && (
        <div className="error submit-error" role="alert">
          {submitError}
        </div>
      )}
      {/* 種別 */}
      {!isEdit && (
        <>
          <RowLabel label={["種別"]} required={true} />
          <div className="control record_type">
            <select
              id="recordType"
              value={recordType}
              onChange={e => {
                setLocked(e.target.value !== 'activity');
                setRecordType(e.target.value);
              }}
            >
              <option value="activity">活動</option>
              <option value="replace">移転</option>
              <option value="norm">ノルマ</option>
              <option value="penalty">ペナルティ</option>
            </select>
          </div>
        </>
      )}

      {/* 内容 */}
      <RowLabel label={["内容"]} required={true} />
      <div className="control content">
        <AutosuggestInput
          items={contentHistory.map(item => item.value)}
          value={content}
          onChange={setContent}
          onSelect={setContent}
          placeholder=""
        />
        {errors.content && <div className="error">{errors.content}</div>}
        {descriptions.map(desc => (
          <div className="desc">{desc}</div>
        ))}
        <RewardGuide result_type="" rules={rules.filter(rule => rule.result_target == 0)} />
      </div>

      {/* Activity */}
      {recordType === 'activity' && (
        <>
          <RowLabel label={["活動", "時間"]} required={minutesEnabled} />
          <div className="control minutes">
            <input
              id="minutes"
              name="minutes"
              type="number"
              autoComplete="on"
              value={minutes}
              disabled={!minutesEnabled}
              onChange={e => setMinutes(e.target.value)}
            />
            {minutesEnabled && (<span> 分</span>)}
            <RewardGuide result_type="活動時間" rules={rules.filter(rule => rule.result_target > 0 && rule.result_type === '活動時間')} />
            {errors.minutes && <div className="error">{errors.minutes}</div>}
          </div>

          <RowLabel label={["取り", "組み"]} required={amountUnit} />
          <div className="control amount">
            <input
              id="amount"
              name="amount"
              type="number"
              autoComplete="on"
              value={amount}
              disabled={!amountUnit}
              onChange={e => setAmount(e.target.value)}
            />
            <span> {amountUnit}</span>
            <RewardGuide result_type="成果量" rules={rules.filter(rule => rule.result_target > 0 && rule.result_type === '成果量')} />
            {errors.amount && <div className="error">{errors.amount}</div>}
          </div>

          <RowLabel label={["延長", "対象"]} required={true} />
          <div className="control credit_type">
            {creditTypeHistory.map((ct, idx) => (
              <label key={idx}>
                <input
                  type="radio"
                  name="creditType"
                  value={ct.value}
                  checked={creditType === ct.value}
                  onChange={() => setCreditType(ct.value)}
                />
                <CreditType type={ct.value}/>
              </label>
            ))}
          </div>

          <RowLabel label={["延長日"]} required={true} />
          <div className="control target_date">
            <select
              name="targetDateSelector"
              value=""
              onChange={(e) => {
                if (e.target.value === "SAT") {
                  setTargetDate(getNextWeekday(6));
                } else if (e.target.value === "SUN") {
                  setTargetDate(getNextWeekday(0));
                } else {
                  const n = parseInt(e.target.value);
                  if (!isNaN(n)) {
                    setTargetDate(formatDate(dayjs().add(n, 'day')));
                  }
                }
              }}
            >
              <option value="">[選択]</option>
              <option value="0">本日</option>
              <option value="1">翌日</option>
              <option value="2">2日後</option>
              <option value="3">3日後</option>
              <option value="SAT">土曜</option>
              <option value="SUN">日曜</option>
            </select>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
            />
            {errors.targetDate && <div className="error">{errors.targetDate}</div>}
          </div>
        </>
      )}

      {/* Replace */}
      {recordType === 'replace' && (
        <>
          <RowLabel label={["移転", "時間"]} required={true} />
          <div className="control minutes">
            <input
              id="minutes"
              name="minutes"
              type="number"
              autoComplete="on"
              value={minutes}
              onChange={e => setMinutes(e.target.value)}
            />
            <span> 分</span>
            {errors.minutes && <div className="error">{errors.minutes}</div>}
          </div>

          <RowLabel label={["移転元", "対象"]} required={true} />
          <div className="control credit_type">
            {creditTypeHistory.map((ct, idx) => (
              <label key={idx}>
                <input
                  type="radio"
                  name="creditType"
                  value={ct.value}
                  checked={creditType === ct.value}
                  onChange={() => setCreditType(ct.value)}
                />
                <CreditType type={ct.value}/>
              </label>
            ))}
          </div>

          <RowLabel label={["移転先", "対象"]} required={true} />
          <div className="control credit_type_rep">
            {creditTypeHistory.map((ct, idx) => (
              <label key={idx}>
                <input
                  type="radio"
                  name="creditTypeRep"
                  value={ct.value}
                  checked={creditTypeRep === ct.value}
                  onChange={() => setCreditTypeRep(ct.value)}
                />
                <CreditType type={ct.value}/>
              </label>
            ))}
            <label key="nosel">
              <input
                type="radio"
                name="creditTypeRep"
                value=""
                checked={!creditTypeRep}
                onChange={() => setCreditTypeRep("")}
              />
              <CreditType type="変更なし"/>
            </label>
          </div>

          <RowLabel label={["移転元", "日"]} required={true} />
          <div className="control target_date">
            <select
              name="targetDateSelector"
              value=""
              onChange={(e) => {
                if (e.target.value === "SAT") {
                  setTargetDate(getNextWeekday(6));
                } else if (e.target.value === "SUN") {
                  setTargetDate(getNextWeekday(0));
                } else {
                  const n = parseInt(e.target.value);
                  if (!isNaN(n)) {
                    setTargetDate(formatDate(dayjs().add(n, 'day')));
                  }
                }
              }}
            >
              <option value="">[選択]</option>
              <option value="0">本日</option>
              <option value="1">翌日</option>
              <option value="2">2日後</option>
              <option value="3">3日後</option>
              <option value="SAT">土曜</option>
              <option value="SUN">日曜</option>
            </select>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
            />
            {errors.targetDate && <div className="error">{errors.targetDate}</div>}
          </div>

          <RowLabel label={["移転先", "日"]} required={false} />
          <div className="control target_date_rep">
            <select
              name="targetDateRepSelector"
              value={targetDateRep === '' ? 'X' : ''}
              onChange={(e) => {
                if (e.target.value === "X") {
                  setTargetDateRep("");
                } else if (e.target.value === "SAT") {
                  setTargetDateRep(getNextWeekday(6));
                } else if (e.target.value === "SUN") {
                  setTargetDateRep(getNextWeekday(0));
                } else {
                  const n = parseInt(e.target.value);
                  if (!isNaN(n)) {
                    setTargetDateRep(formatDate(dayjs().add(n, 'day')));
                  }
                }
              }}
            >
              <option value="">[選択]</option>
              <option value="X">変更なし</option>
              <option value="0">本日</option>
              <option value="1">翌日</option>
              <option value="2">2日後</option>
              <option value="3">3日後</option>
              <option value="SAT">土曜</option>
              <option value="SUN">日曜</option>
            </select>
            <input
              type="date"
              value={targetDateRep}
              onChange={e => setTargetDateRep(e.target.value)}
            />
            {errors.targetDateRep && <div className="error">{errors.targetDateRep}</div>}
          </div>
        </>
      )}

      {/* Norm */}
      {recordType === 'norm' && (
        <>
          <RowLabel label={["完了時"]} required={false} />
          <div className="control norm_target">
            <AutosuggestInput
              items={contentHistoryActivity.map(item => item.value)}
              value={normTarget}
              onChange={setNormTarget}
              onSelect={setNormTarget}
              placeholder=""
            />
            {errors.normTarget && <div className="error">{errors.normTarget}</div>}
          </div>

          {isEdit && isAdmin && (
            <>
              <label>完了</label>
              <div className="control norm_done">
                <input
                  type="checkbox"
                  checked={normDone}
                  onChange={e => setNormDone(e.target.checked)}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Penalty */}
      {recordType === 'penalty' && (
        <>
          <RowLabel label={["減算", "時間"]} required={true} />
          <div className="control">
            <div className="penalty_amount">
              <label key="0">
                <input
                  type="radio"
                  name="penaltyType"
                  value="0"
                  checked={penaltyType === 0}
                  onChange={() => setPenaltyType(0)}
                />
                <input
                  type="number"
                  name="penalty_amount"
                  value={penaltyAmount}
                  autoComplete="on"
                  onChange={e => setPenaltyAmount(e.target.value)}
                  disabled={penaltyType !== 0}
                />
                分 減算
              </label>
            </div>
            <div className="penalty_rate">
              <label key="1">
                <input
                  type="radio"
                  name="penaltyType"
                  value="1"
                  checked={penaltyType === 1}
                  onChange={() => setPenaltyType(1)}
                />
                <input
                  type="number"
                  name="penalty_rate"
                  value={penaltyRate}
                  autoComplete="on"
                  onChange={e => setPenaltyRate(e.target.value)}
                  disabled={penaltyType !== 1}
                />
                % 効率低下
              </label>
              <select
                name="penaltyPeriod"
                value={penaltyPeriod}
                onChange={(e) => setPenaltyPeriod(e.target.value)}
                disabled={penaltyType !== 1}
              >
                <option value="1">1日間</option>
                <option value="2">2日間</option>
                <option value="3">3日間</option>
                <option value="4">4日間</option>
                <option value="5">5日間</option>
                <option value="6">6日間</option>
                <option value="7">7日間</option>
                <option value="14">14日間</option>
                <option value="30">30日間</option>
              </select>
            </div>
            {errors.penaltyAmount && <div className="error">{errors.penaltyAmount}</div>}
          </div>
        </>
      )}

      {isAdmin && recordType === 'activity' && (
        <>
          <RowLabel label={["実施日"]} required={false} />
          <div className="control start_time">
            <input
              id="startTime"
              type="datetime-local"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
            />
          </div>
        </>
      )}
      {isAdmin && (recordType === 'norm' || recordType === 'penalty') && (
        <>
          <RowLabel label={["発動", "時刻"]} required={false} />
          <div className="control start_time">
            <select
              name="startTimeSelector"
              value={startTimeSelector ? "enabled" : "disabled"}
              onChange={(e) => {
                if (e.target.value === "enabled") {
                  setStartTimeSelector(true);
                } else if (e.target.value === "disabled") {
                  setStartTime(formatDatetime(dayjs()));
                  setStartTimeSelector(false);
                } else {
                  var n = parseInt(e.target.value);
                  if (n >= 0) {
                    setStartTime(formatDatetime(dayjs().add(n, 'hour')));
                  } else {
                    setStartTime(formatDatetime(dayjs().add(-n, 'hour').startOf('day')));
                  }
                  setStartTimeSelector(true);
                }
              }}
            >
              {penaltyType === 1 ? (<>
                <option value="disabled">今日から</option>
                <option value="enabled">指定日</option>
                <option value="-24">明日以降</option>
                <option value="-48">2日後以降</option>
                <option value="-72">3日後以降</option>
                <option value="-168">7日後以降</option>
              </>) : (<>
                <option value="disabled">今から</option>
                <option value="enabled">指定時刻</option>
                <option value="6">6時間後以降</option>
                <option value="12">12時間後以降</option>
                <option value="18">18時間後以降</option>
                <option value="24">24時間後以降</option>
                <option value="-1">本日以降</option>
                <option value="-24">明日以降</option>
                <option value="-48">2日後以降</option>
                <option value="-72">3日後以降</option>
                <option value="-168">7日後以降</option>
              </>)}
            </select>
            <input
              id="startTime"
              name="startTime"
              type={penaltyType === 1 ? "date" : "datetime-local"}
              value={penaltyType === 1 ? formatDate(startTime) : startTime}
              autoComplete="on"
              onChange={e => setStartTime(e.target.value + (penaltyType === 1 ? ' 00:00' : ''))}
              disabled={!startTimeSelector}
            />
          </div>
        </>
      )}
      {isAdmin && recordType === 'norm' && (
        <>
          <RowLabel label={["終了", "時刻"]} required={false} />
          <div className="control end_time">
            <select
              name="endTimeSelector"
              value={endTimeSelector ? "enabled" : "disabled"}
              onChange={(e) => {
                if (e.target.value === "enabled") {
                  setEndTimeSelector(true);
                } else if (e.target.value === "disabled") {
                  setEndTimeSelector(false);
                } else {
                  const base = startTimeSelector ? dayjs(startTime) : dayjs();
                  setEndTime(formatDatetime(base.add(parseInt(e.target.value), 'hour')));
                  setEndTimeSelector(true);
                }
              }}
            >
              <option value="disabled">無期限</option>
              <option value="enabled">有効</option>
              <option value="6">有効: 6時間</option>
              <option value="12">有効: 12時間</option>
              <option value="18">有効: 18時間</option>
              <option value="24">有効: 24時間</option>
              <option value="36">有効: 36時間</option>
              <option value="48">有効: 48時間</option>
              <option value="72">有効: 3日間</option>
              <option value="168">有効: 7日間</option>
            </select>
            <input
              id="endTime"
              name="endTime"
              type="datetime-local"
              value={endTime}
              autoComplete="on"
              onChange={e => setEndTime(e.target.value)}
              disabled={!endTimeSelector}
            />
          </div>
        </>
      )}

      {isAdmin && (
        <>
          <RowLabel label={["ロック"]} required={false} />
          <div className="control locked">
            <input
              type="checkbox"
              checked={locked}
              onChange={e => setLocked(e.target.checked)}
            />
          </div>
        </>
      )}

      {isAdmin && recordType !== 'penalty' && (
        <>
          <RowLabel label={["評価"]} required={false} />
          <div className="control evaluation">
            <input
              name="evaluation"
              type="text"
              value={evaluation}
              autoComplete="on"
              onChange={e => setEvaluation(e.target.value)}
            />
          </div>
        </>
      )}

      <div className="form-actions">
        <button type="submit" disabled={requesting || uiLocked}>{isEdit ? '更新' : '登録'}</button>
        {isEdit && <button type="button" onClick={onCancel}>キャンセル</button>}
      </div>
    </form>
  );
}
